import { AlertTriangle, ShieldCheck } from "lucide-react";
import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import type {
  ScopeActivationResponse,
  ScopeSummary,
} from "@/api/scope-activation";
import { StackedBar } from "@/components/projects/diagnostic/primitives/StackedBar";
import { formatCount } from "@/lib/format";

import {
  countryAvailableItems,
  lowConfidenceItems,
  type ReasonCount,
  type Suggestion,
  type SuggestionCounts,
  type SuggestionLob,
  SUGGESTION_META,
  SUGGESTION_ORDER,
} from "./aderenciaData";

/**
 * Barra empilhada da Sugestão (activate/decision/out_of_scope).
 *
 * Virou adaptador: o desenho está na primitiva `StackedBar` (DIAG.2); aqui fica
 * só a tradução de `SuggestionCounts` para os segmentos genéricos.
 */
function SuggestionBar({
  counts,
  height = "h-5",
}: {
  counts: SuggestionCounts;
  height?: string;
}) {
  const { t } = useTranslation();
  const segments = SUGGESTION_ORDER.map((s) => ({
    key: s,
    label: t(SUGGESTION_META[s].label),
    className: SUGGESTION_META[s].bar,
    value: counts[s] ?? 0,
  }));
  return <StackedBar segments={segments} height={height} />;
}

function LobRow({ b }: { b: SuggestionLob }) {
  const { t } = useTranslation();
  const ratio = b.total > 0 ? b.decisions / b.total : 0;
  const hot = ratio >= 0.4 && b.decisions > 0;
  return (
    <div className="grid grid-cols-[10rem_1fr_5rem] items-center gap-3">
      <div className="text-right">
        <div className="truncate text-xs font-semibold">{b.lob}</div>
        <div className="font-mono text-[10px] text-muted-foreground">
          {t("{{count}} items", { count: b.total })}
        </div>
      </div>
      <div className={hot ? "rounded ring-2 ring-amber-300/60" : ""}>
        <SuggestionBar counts={b.counts} />
      </div>
      <div className="text-left font-mono text-xs font-bold text-amber-600">
        {b.decisions || <span className="text-muted-foreground">0</span>}
        {hot && (
          <span className="ml-1 rounded bg-amber-100 px-1 py-px text-[9px] font-bold text-amber-700">
            {Math.round(ratio * 100)}%
          </span>
        )}
      </div>
    </div>
  );
}

/** Para onde cada totalizador da Visão Geral leva quando é clicado. */
export type OverviewDrillTarget = "decisions" | "clean_core" | "low_confidence";

/**
 * Um totalizador da Visão Geral. Com destino, vira botão e abre a lista dos
 * itens que ele conta (pedido do consultor da AL5, 16/10/2026: "um drill down
 * nestes totalizadores para sabermos a origem"). Sem destino, segue estático.
 */
function Totalizer({
  testId,
  className,
  onClick,
  children,
}: {
  testId: string;
  className: string;
  onClick?: () => void;
  children: ReactNode;
}) {
  if (!onClick) {
    return (
      <div data-testid={testId} className={className}>
        {children}
      </div>
    );
  }
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`${className} text-left transition hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
    >
      {children}
    </button>
  );
}

export function OverviewView({
  data,
  summary: agg,
  countryOnly = true,
  onDrill,
}: {
  data: ScopeActivationResponse;
  // Agregado server-side pelo veredito EFETIVO (reflete edições + aprovação).
  summary?: ScopeSummary;
  countryOnly?: boolean;
  /** Abre o Catálogo filtrado pelo que o totalizador conta. */
  onDrill?: (target: OverviewDrillTarget) => void;
}) {
  const { t } = useTranslation();
  // Eixo protagonista: o veredito EFETIVO (analista → senão sugestão), agregado
  // no servidor com o filtro de país. O toggle "só disponível no país" e o
  // execution vêm do pai; o `data` do run fica só pra evidência ECC secundária.
  const counts: SuggestionCounts = useMemo(
    () => ({
      activate: agg?.counts.activate ?? 0,
      decision: agg?.counts.decision ?? 0,
      out_of_scope: agg?.counts.out_of_scope ?? 0,
    }),
    [agg],
  );
  const reasons: ReasonCount[] = useMemo(
    () =>
      (agg?.reasons ?? []).map((r) => ({
        suggestion: r.verdict as Suggestion,
        reason: r.reason,
        count: r.count,
      })),
    [agg],
  );
  const lobs: SuggestionLob[] = useMemo(
    () =>
      (agg?.lobs ?? []).map((l) => ({
        lob: l.lob,
        total: l.total,
        counts: {
          activate: l.activate,
          decision: l.decision,
          out_of_scope: l.out_of_scope,
        },
        decisions: l.decision,
      })),
    [agg],
  );
  const total = agg?.total ?? 0;

  // Filtro de itens por país (escopo do servidor). Centralizado aqui pois atende à linha de confiança e à seção Clean Core.
  const countryItems = useMemo(
    () => countryAvailableItems(data.items, countryOnly),
    [data.items, countryOnly],
  );

  // Usa `countryItems` para sincronizar o total com o ERP fit/toggle de país, sem esperar o resumo do servidor.
  // `suggested` usa o conjunto cru para validar a existência de veredito no run global.
  const assessed = countryItems.length;
  const suggested = data.items.some((i) => i.suggestion != null);

  // Fit-to-Standard = activate / (activate + decision). Out of scope NÃO entra
  // na conta (é mostrado à parte); sobe conforme o analista resolve decisões.
  const inScope = counts.activate + counts.decision;
  // Exibe travessão em vez de 0 para `inScope` ausente (assim como no `progressPct`) para evitar interpretações erradas.
  const fitPct =
    inScope > 0 ? Math.round((counts.activate / inScope) * 100) : null;
  const outOfScope = counts.out_of_scope;

  // Progresso da revisão: pontos de decisão da árvore que o analista já resolveu.
  const decTotal = agg?.decisions_total ?? 0;
  const decResolved = agg?.decisions_resolved ?? 0;
  // Trata denominador zero (`decisions_total = 0`) como nulo/sem medição para evitar falsa conclusão de revisão.
  const progressPct =
    decTotal > 0 ? Math.round((decResolved / decTotal) * 100) : null;

  // Eixo ECC (verdict) vira evidência secundária: activate no país / total.
  const eccActivate = agg?.ecc_activate ?? 0;
  const eccFit = total > 0 ? Math.round((eccActivate / total) * 100) : null;

  // Clean Core / baixa confiança são sinais do run (não mudam com o veredito);
  // seguem vindo do payload, respeitando o mesmo filtro de país (`countryItems`).
  const cleanCore = useMemo(
    () => countryItems.filter((i) => i.clean_core).length,
    [countryItems],
  );
  const lowConf = useMemo(
    () => lowConfidenceItems(countryItems).length,
    [countryItems],
  );

  const hot = lobs.filter((b) => b.total > 0 && b.decisions / b.total >= 0.4);
  const rest = lobs.filter((b) => !(b.total > 0 && b.decisions / b.total >= 0.4));
  const restCounts = rest.reduce<SuggestionCounts>(
    (acc, b) => {
      acc.activate += b.counts.activate;
      acc.decision += b.counts.decision;
      acc.out_of_scope += b.counts.out_of_scope;
      return acc;
    },
    { activate: 0, decision: 0, out_of_scope: 0 },
  );
  const restTotal = rest.reduce((acc, b) => acc + b.total, 0);
  const restDecisions = rest.reduce((acc, b) => acc + b.decisions, 0);

  return (
    <div className="flex flex-col gap-6">
      {/* linha de confiança / trust */}
      <div className="font-mono text-[11px] text-muted-foreground">
        {t("{{count}} tables probed", { count: formatCount(data.fingerprint_tables) })} ·{" "}
        <span className="font-semibold text-foreground">
          {formatCount(assessed)}
        </span>{" "}
        {t("items · rules v{{version}}", { version: data.version })} ·{" "}
        {/* Se o run não possui recomendações, ignora a qualificação para evitar validar um resultado inexistente. */}
        {suggested ? t("deterministic") : t("assessment unavailable")}
      </div>

      {/* Leitura recusada pelo SAP: sem isto, o que não foi lido parece "não usa". */}
      {(data.fingerprint_refused_tables ?? 0) > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            {t(
              "SAP did not release reading {{refused}} of the {{count}} probed tables. Items that depend on them went to Decision: release the reading for the RFC user and run again.",
              {
                refused: formatCount(data.fingerprint_refused_tables ?? 0),
                count: formatCount(data.fingerprint_tables),
              },
            )}
          </span>
        </div>
      )}

      {/* headline: um número — Fit-to-Standard.
          Base = escopo relevante (activate + decision); out of scope fora. */}
      <div className="flex flex-col gap-4">
        {/* Move o progresso da revisão para os detalhes, mantendo a headline focada exclusivamente no resultado do run. */}
        <div className="flex flex-col gap-2">
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-extrabold tracking-tight text-emerald-600">
              {fitPct != null ? `${fitPct}%` : "—"}
            </span>
            <span className="text-sm font-semibold">Fit-to-Standard</span>
          </div>
          <p className="text-xs text-muted-foreground">
            {t("{{activate}} activate of {{scope}} in scope (activate + decision).", {
              activate: formatCount(counts.activate),
              scope: formatCount(inScope),
            })}
          </p>
          <div className="flex h-3 w-full overflow-hidden rounded bg-muted">
            <div
              className="bg-emerald-500"
              style={{ flexGrow: counts.activate, flexBasis: 0 }}
              title={t("{{count}} activate", { count: counts.activate })}
            />
            <div
              className="bg-amber-500"
              style={{ flexGrow: counts.decision, flexBasis: 0 }}
              title={t("{{count}} decision", { count: counts.decision })}
            />
          </div>
        </div>

        {/* Detalhe: o que saiu da headline, o out of scope e o eixo ERP como
            evidência. Cada um mantém o denominador que já declarava. */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[10px] text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-sm bg-gray-300" />
            {t("Out of scope · {{count}} (shown, not counted)", { count: formatCount(outOfScope) })}
          </span>
          <span className="text-muted-foreground/80">
            {/* Rótulo e valor separados em nós próprios para manter a compatibilidade com os testes que buscam o texto exato. */}
            <span>{t("Review progress")}</span>:{" "}
            <span className="font-semibold text-sky-600">
              {progressPct != null ? `${progressPct}%` : "—"}
            </span>{" "}
            ·{" "}
            {t("{{resolved}} of {{total}} decision points resolved.", {
              resolved: formatCount(decResolved),
              total: formatCount(decTotal),
            })}
          </span>
          <span className="text-muted-foreground/80">
            {/* Usa `·` em vez de `—` para evitar o par "— —" em valores sem denominador e padronizar com a linha. */}
            {t("ERP fit (evidence): {{pct}}", { pct: eccFit != null ? `${eccFit}%` : "—" })} ·{" "}
            {t("{{active}} of {{total}} used in the source system.", {
              active: formatCount(eccActivate),
              total: formatCount(total),
            })}
          </span>
        </div>
      </div>

      {/* POR QUÊ: motivo de cada decisão (o que o Marco precisa justificar) */}
      <div className="flex flex-col gap-2">
        <div className="text-sm font-semibold">{t("Why each decision")}</div>
        <p className="text-xs text-muted-foreground">
          {t('Every item is classified by the decision tree. This is the answer to "why" for the client conversation.')}
        </p>
        <div className="mt-1 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
          {reasons.map((r) => (
            <div
              key={`${r.suggestion}-${r.reason}`}
              className="flex items-center gap-2 rounded border bg-white px-3 py-1.5 text-xs"
            >
              <span
                className={`inline-block h-2.5 w-2.5 flex-shrink-0 rounded-sm ${SUGGESTION_META[r.suggestion as Suggestion].bar}`}
              />
              <span className="flex-1 truncate">{r.reason}</span>
              <span className="font-mono font-bold">{r.count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* callout "Need your attention" (decisões) + Clean Core + low confidence */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Totalizer
          testId="fts-card-attention"
          className="rounded-lg border border-amber-300 bg-amber-50 p-4"
          onClick={onDrill && (() => onDrill("decisions"))}
        >
          <div className="text-3xl font-extrabold text-amber-600">
            {counts.decision}
          </div>
          <div className="font-mono text-[10px] uppercase tracking-wide text-amber-700">
            {t("Need your attention")}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {t('Items suggested as "Decision" — out of baseline, the analyst confirms with the client.')}
          </div>
        </Totalizer>
        <Totalizer
          testId="fts-card-clean-core"
          className="rounded-lg border bg-white p-4"
          onClick={onDrill && (() => onDrill("clean_core"))}
        >
          <div className="flex items-center gap-1.5 text-3xl font-extrabold text-violet-600">
            <ShieldCheck className="h-5 w-5" />
            {cleanCore}
          </div>
          <div className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
            Clean Core
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {t("Items that touch the extension strategy — review before activating.")}
          </div>
        </Totalizer>
        <Totalizer
          testId="fts-card-low-confidence"
          className="rounded-lg border bg-white p-4"
          onClick={onDrill && (() => onDrill("low_confidence"))}
        >
          <div className="flex items-center gap-1.5 text-3xl font-extrabold text-orange-600">
            <AlertTriangle className="h-5 w-5" />
            {lowConf}
          </div>
          <div className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
            {t("Low confidence")}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {t("Ambiguous signal — they need a second look before the workshop.")}
          </div>
        </Totalizer>
      </div>

      {/* mapa por LoB (Sugestão), ordenado por densidade de decisão */}
      <div className="flex flex-col gap-2">
        <div className="text-sm font-semibold">
          {t("Decisions are concentrated, not scattered")}
        </div>
        <p className="text-xs text-muted-foreground">
          {t("Sorted by decision density, not size. This is where the analyst starts the Fit-to-Standard workshop prep.")}
        </p>
        <div className="mt-3 flex flex-col gap-2.5">
          {hot.map((b) => (
            <LobRow key={b.lob} b={b} />
          ))}
          {rest.length > 0 && (
            <div className="grid grid-cols-[10rem_1fr_5rem] items-center gap-3 opacity-70">
              <div className="text-right text-xs font-semibold">
                {t("Other {{count}} LoBs", { count: rest.length })}
                <div className="font-mono text-[10px] font-normal text-muted-foreground">
                  {t("{{count}} items", { count: restTotal })}
                </div>
              </div>
              <SuggestionBar counts={restCounts} />
              <div className="text-left font-mono text-xs font-bold text-muted-foreground">
                {restDecisions}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
